/**
 * Which failure of a launch-time convenience request may be ignored.
 *
 * Its own module with no imports, for the same reason as `visibility-intent.ts`
 * and `nearby-recovery.ts`: it encodes a rule that a plain `node --test` can
 * drive, and it was got wrong in exactly the way an untested rule gets got
 * wrong — by being written as `catch {}`.
 *
 * ## The bug this exists to prevent
 *
 * `hydrate` makes two PATCHes nobody asked for: it finishes an onboarding the
 * device believes it already walked through, and it syncs the account's locale
 * to the app's. Neither may fail the launch — that was the previous fix, and it
 * was right. Both were written as a bare catch, which is a different and wider
 * claim: that no failure of those calls means anything.
 *
 * One does. A 401 has already begun sign-out by the time it is thrown
 * (`onUnauthorized?.()` in `api/client.ts`, deliberately not awaited), and
 * `clearLocalSession` sets `unauthenticated` synchronously before its first
 * await. Swallowing the error let `hydrate` carry on and overwrite that with
 * `status: "authenticated"` and `session: get().session` — by then null. The end
 * state is the signed-in UI with no token, on which every request fails, and a
 * user written back to disk after sign-out had removed it. Not a race: the
 * ordering is fixed, so it happened every time.
 *
 * ## The rule
 *
 * Ignore the server REFUSING the request. Never ignore the session being gone,
 * the API being unreachable, or the server failing — those have correct handling
 * in `hydrate`'s outer catch, and reaching it is the whole point of rethrowing.
 */

/**
 * A refusal of the request itself, as opposed to anything about the session.
 *
 * 400 is the granularity, not a message match. On these endpoints with these
 * bodies the server can only refuse for the 18+ gate or DTO validation, and both
 * mean the same thing to a launch: do not apply this change, carry on with the
 * account we already read, and let routing send the user wherever that account
 * actually belongs.
 *
 * Structurally typed rather than importing `ApiError`, so this module keeps no
 * dependency on the API client — and so a thrown value that merely looks like an
 * error cannot slip through on `instanceof` alone.
 */
export function isIgnorableRefusal(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  return (error as { status?: unknown }).status === 400;
}
